import Input from "../Input/Input"
import ModalComponent from "../Modal/Modal"
import React, { useEffect, useState } from "react"
import { useMyContext } from "../MyContextProvider/MyContextProvider"
import Dropdown from "../Dropdown/Dropdown"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { ESIGN_POPUP_AFFILIATE_BILLING, PCA_FOR_AFFILIATES } from "../../config/featureFlagKeys"
import { getDebtsDropDown } from "../../features/creditReports/creditReports"
import { getAffiliateBilling, getBankAccountsTypes } from "../../features/payment/paymentSlice"
import { getLeadDetails } from "../../features/contacts/contacts"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { isEmptyFields } from "../../helpers/helper"
import { AGREEMENT_NEEDS_ACCOUNT, AGREEMENT_TEMPLATES } from "../DocsTab/DocsTabTable/esignTemplates"
import useLsaChargeName from "../../hooks/useLsaChargeName"
import LsaChargeNamePicker from "../LsaChargeName/LsaChargeNamePicker"


// WS #2228909. The amount a rep may type for a replacement card's PCA: empty, or a positive amount
// with at most cents ("$2,500.00" accepted). The server checks the same.
const pcaAmountError = (text) => {
  const cleaned = String(text ?? '').trim().replace(/^\$\s*/, '').replace(/,/g, '')
  if (cleaned === '') return ''
  return /^\d+(\.\d{1,2})?$/.test(cleaned) && Number(cleaned) > 0 ? '' : 'Enter a positive amount, at most two decimals'
}

// WS #2228909 point 4. A card in the client's split that belongs to someone else gets that holder's own
// Card Payment Authorization, never a PCA the client signs. The ids of those cards, or an empty set when
// the split cannot be read (the server refuses such a PCA anyway, so the popup is not blocked).
const OTHER_HOLDER = 'other'
const holderCardIds = (result) => {
  if (result?.meta?.requestStatus !== 'fulfilled') return new Set()
  const split = result.payload?.data?.programPlan?.cardSplit
  return new Set((Array.isArray(split) ? split : [])
    .filter((row) => row?.holder === OTHER_HOLDER && row?.billingAccount)
    .map((row) => String(row.billingAccount)))
}

// WS #1944881. The server prepares none of these without an account. A client billed to the affiliate
// has no bank account or card, so the popup sends their affiliate method. The POAs print no account;
// the agreements print the payment section blank for it.
const AFFILIATE_FALLBACK_DOCS = ['poa', 'revoked-poa', ...AGREEMENT_TEMPLATES]

const withoutCards = (billing, ids) => (ids.size === 0 ? billing : {
  ...billing,
  creditCards: (billing?.creditCards || []).filter((card) => !ids.has(String(card.value))),
})

const FloatingPopup = ({ templateData, setProceed, userId, signingLinkOn = false }) => {
  const dispatch = useDispatch()
  const { data, updateData } = useMyContext()
  const { triggerLoading } = useMyContextLoading()
  const [openModal, setOpenModal] = useState(false)
  const obj = data?.data?.data
  const [formData, setFormData] = useState({
    ...obj,
    dob: obj?.dob?.split('T')[0],
    debtId: '',
    billingAccountId: '',
  })
  const [billing, setBilling] = useState({})
  const [billingLoaded, setBillingLoaded] = useState(false)
  const [debtsDropDown, setDebtsDropDown] = useState([])
  const [holderCards, setHolderCards] = useState(new Set())
  const [affiliateAccountId, setAffiliateAccountId] = useState('')
  // WS #2228909. A PCA is for a card the rep chooses (a replacement after a decline), with that card's
  // amount if they type one. It used to show no fields and use the client's primary account.
  const pcaForAffiliates = useSelector(selectFeatureFlag(PCA_FOR_AFFILIATES))
  const isPca = pcaForAffiliates && templateData?.typeDoc === 'pca'
  const pcaBilling = isPca ? withoutCards(billing, holderCards) : billing
  const affiliateFallback = useSelector(selectFeatureFlag(ESIGN_POPUP_AFFILIATE_BILLING))
    && AFFILIATE_FALLBACK_DOCS.includes(templateData?.typeDoc)
  const isAgreement = AGREEMENT_TEMPLATES.includes(templateData?.typeDoc)
  const chargeName = useLsaChargeName(isAgreement ? userId : null)
  const chargeNameBlocked = isAgreement && !!chargeName.blocked

  useEffect(() => {
    if (!isPca || !userId) return
    let live = true
    Promise.resolve(dispatch(getLeadDetails(userId)))
      .then((result) => { if (live) setHolderCards(holderCardIds(result)) })
      .catch(() => {})
    return () => { live = false }
  }, [dispatch, isPca, userId])

  // The default must not be someone else's card: the client's primary among the rest, else the first.
  useEffect(() => {
    if (!isPca || !holderCards.has(String(formData.billingAccountId))) return
    const rest = withoutCards(billing, holderCards)
    const own = [...(rest?.bankAccounts || []), ...(rest?.creditCards || [])]
    const next = own.find((account) => account.isPrimary) || own[0]
    setFormData((current) => ({ ...current, billingAccountId: next ? next.value : '' }))
  }, [isPca, holderCards, billing, formData.billingAccountId])

  useEffect(() => {
    if (templateData?.status && templateData?.view === 'show') {
      setOpenModal(true)
    } else if (!templateData?.status && templateData?.view !== 'show') {
      setOpenModal(false)
    }
  }, [templateData])

  useEffect(() => {
    const fetch = async () => {
      await handleDebtsDropDown()
    }
    fetch()
  }, [])

  const handleInputChange = ({ name, value }) => {
    setFormData((formData) => ({ ...formData, [name]: value }))
  }

  const handleLoadingData = async ({ billing, debtsDropDown }) => {
    const allBillingAccounts = [...(billing?.bankAccounts || []), ...(billing?.creditCards || [])]
    const primaryAccount = allBillingAccounts.find(account => account.isPrimary)

    if (primaryAccount) {
      setFormData((formData) => ({ ...formData, ['billingAccountId']: primaryAccount.value }))
    } else if (allBillingAccounts.length > 0) {
      setFormData((formData) => ({ ...formData, ['billingAccountId']: allBillingAccounts[0]?.value }))
    }

    if (debtsDropDown?.length > 0) {
      setFormData((formData) => ({ ...formData, ['debtId']: debtsDropDown[0]?.value }))
    }
  }

  const handleDebtsDropDown = async () => {
    triggerLoading(true)

    let result = await dispatch(getDebtsDropDown(userId))
    const billingResult = await dispatch(getBankAccountsTypes(userId))

    setBilling(billingResult.payload.data)
    setDebtsDropDown(result?.payload?.data)
    await handleLoadingData({ billing: billingResult.payload.data, debtsDropDown: result?.payload?.data })

    const accounts = [...(billingResult?.payload?.data?.bankAccounts || []), ...(billingResult?.payload?.data?.creditCards || [])]
    if (affiliateFallback && accounts.length === 0) {
      const affiliate = await dispatch(getAffiliateBilling(userId))
      const affiliateId = affiliate?.payload?.data?._id
      if (affiliateId) {
        setAffiliateAccountId(affiliateId)
        setFormData((formData) => ({ ...formData, billingAccountId: affiliateId }))
      }
    }
    // Set after the affiliate method is read, so the agreement does not flash "needs an account" first.
    if (billingResult?.payload?.data && typeof billingResult.payload.data === 'object') setBillingLoaded(true)

    triggerLoading(false)
  }

  const hideModal = () => {
    updateData({ ...data, view: 'hide' })
    setOpenModal(false)
  }
  const closeModal = () => {
    updateData({ ...data, status: false, view: 'hide' })
    setOpenModal(false)
  }

  const proceed = () => {
    setProceed({ formData, isReady: true, oldData: data })
  }
  const isEmptyTemplateData = templateData?.data?.data && Object.keys(templateData.data.data).length === 0
  const amountError = isPca ? pcaAmountError(formData.pcaAmount) : ''
  const isDisabledSubmitButton = isPca
    ? isEmptyFields(formData.billingAccountId) || !!amountError
    : !isEmptyTemplateData && (isEmptyFields(formData.debtId) || isEmptyFields(formData.billingAccountId))
  const hasAccountOnFile = (billing?.bankAccounts?.length || 0) + (billing?.creditCards?.length || 0) > 0
  const needsAccount = signingLinkOn && AGREEMENT_TEMPLATES.includes(templateData?.typeDoc) && billingLoaded
    && !hasAccountOnFile && !affiliateAccountId
  return (
    <div>
      <ModalComponent customOpenModal={openModal}
        floatingBtn={true}
        disableBtn={false}
        setHidePopup={hideModal}
        modalButtonTitle="Proceed"
        title="Warning!"
        functionButton={() => proceed()}
        onClose={(v) => closeModal()}
        buttonClasses="flex h-9 items-center"
        disabled={isDisabledSubmitButton || needsAccount || chargeNameBlocked}
      >
        <div className="max-w-[630px]">
          {isAgreement && chargeName.required && (
            <div className="mb-6">
              <LsaChargeNamePicker userId={userId} />
            </div>
          )}
          {isPca ? (
            <div className="grid gap-x-4 gap-y-6 mb-6">
              <Dropdown
                small={false}
                setValue={(e) => handleInputChange({ name: 'billingAccountId', value: e })}
                value={formData['billingAccountId']}
                menuItems={pcaBilling}
                myListSubheader={[
                  { name: 'Bank Accounts', value: 'bankAccounts' },
                  { name: 'Credit Cards', value: 'creditCards' }
                ]}
                label="Billing Account *"
                placeholder="Select a value"
              />
              <Input
                width="w-[300px]"
                placeholder="$"
                label="Amount for this card (optional)"
                value={formData.pcaAmount ?? ''}
                onChange={(e) => handleInputChange({ name: 'pcaAmount', value: e.target.value })}
                errorMsg={amountError}
              />
              <div className="text-text-secondary text-sm">
                The addendum prints this card and, if you enter one, this amount as a one-time payment.
                Leave it empty and the addendum shows no amount.
              </div>
            </div>
          ) : isEmptyTemplateData ? (
            <div className="text-text-secondary italic">
              {needsAccount
                ? AGREEMENT_NEEDS_ACCOUNT
                : 'All necessary data is being handled automatically by the system — feel free to proceed.'}
            </div>
          ) : (
            <>
              <div className="grid gap-x-4 gap-y-6 mb-6">
                <Dropdown
                  small={false}
                  setValue={(e) => handleInputChange({ name: 'debtId', value: e })}
                  value={formData['debtId']}
                  menuItems={debtsDropDown}
                  label="Debt *"
                  placeholder="Bank Name / Account Number / Original Loan Amount"
                />
                <Dropdown
                  small={false}
                  setValue={(e) => handleInputChange({ name: 'billingAccountId', value: e })}
                  value={formData['billingAccountId']}
                  menuItems={billing}
                  myListSubheader={[
                    { name: 'Bank Accounts', value: 'bankAccounts' },
                    { name: 'Credit Cards', value: 'creditCards' }
                  ]}
                  label="Billing Account *"
                  placeholder="Select a value"
                />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-6 mb-6">
                {obj && typeof obj === 'object' && Object.keys(obj).map((key, index) => (
                  <div key={index}>
                    <Input
                      width="w-[300px]"
                      placeholder=""
                      label={'{' + key + '}'}
                      value={formData[key]}
                      onChange={(e) =>
                        handleInputChange({ name: key, value: e.target.value })
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </ModalComponent>
    </div>
  )
}
export default FloatingPopup
